import { DOCUMENT } from "@angular/common";
import {
  Inject,
  Injectable,
  computed,
  inject,
  signal,
} from "@angular/core";
import { AsyncSubject, Subject, TimeoutError, catchError, defer, finalize, lastValueFrom, of, timeout } from "rxjs";
import { SharedService } from "./shared.service";
import { SharedApiService } from "./api/sharedApi.service";
import {
  IExecuteProcedureResult,
  IGetSobitTokenResultData,
  IPaymentAttribute,
  ISobitConfig,
 ResultCode } from "./model/pos.api.model";
import { PopUpDialogInformalService } from "./confirms/popUpDialogInformal.service";
import { PopUpInformalType } from "../components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { NgxSpinnerService } from "ngx-spinner";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { LoginService } from "./login.service";
import { IPayment } from "./model/get-trip-data.model";
import { PaymentStatusEnum } from "../enums/payment-status.enum";
import { TenderType } from "./model/tender-type.enum";
import { isNullOrUndefined } from "../functions/common-functions";
import { PaymentStatusCheckOutcome, SobitErrorCode , UpdatePaymentStatusModel } from "./model/sobit.model";

import { EftLogService } from "./eftlog.service";

import { IGetUpdateDertPaymentParams } from "./model/sp-procedures-params.model";
import { PayByCardRequest, PayByCardResult, SobitRequestData } from "../models/sobit-request-data";

interface ISobitLogContext {
  bnr?: string | number | null;
  variableNumber?: string | number | null;
  voucherNumber?: string | number | null;
}

interface ISobitCheckTimeoutResponse {
  __sobitTimeout: true;
}

interface ISobitPaymentLock {
  paymentUID: string;
  ownerId: string;
  createdAt: number;
}

interface ISobitPaymentSentMarker {
  paymentUID: string;
  createdAt: number;
}

interface ILockManagerLike {
  request<T>(name: string, options: { mode: "exclusive" }, callback: () => Promise<T>): Promise<T>;
}

declare let SobitEcr: any;


/*
* Service responsible for handling interactions with Sobit ECR, including loading the Sobit script, generating the client, and processing card payments. 
* It also implements a locking mechanism to prevent concurrent payment attempts for the same paymentUID across multiple browser tabs/windows. 
* The service logs important events and errors using EftLogService and displays user-friendly messages using PopUpDialogInformalService when errors occur.
*/
@Injectable({
  providedIn: "root",
})
export class SobitService {
  constructor(@Inject(DOCUMENT) private document: Document) {}
  private _sharedService: SharedService = inject(SharedService);
  private _sharedApiService: SharedApiService = inject(SharedApiService);
  private _popUpDialogInformalService = inject(PopUpDialogInformalService);
  private _loginService = inject(LoginService);
  private readonly _eftLogService = inject(EftLogService);
  private readonly spinner = inject(NgxSpinnerService);
  private translate = inject(TranslateService);
  private readonly router = inject(Router);
  private _sobitClient: any;
  private readonly _paymentsRefreshRequested = new Subject<void>();

  readonly paymentsRefreshRequested$ = this._paymentsRefreshRequested.asObservable();

  private readonly _paymentLockStoragePrefix = "dert-pos:sobit-payment-lock:";
  private readonly _paymentSentStoragePrefix = "dert-pos:sobit-payment-sent:";
  private readonly _paymentLockMaxAgeMs = 30 * 60 * 1000;
  private readonly _paymentSentMarkerMaxAgeMs = 7 * 24 * 60 * 60 * 1000;
  private readonly _paymentLockOwnerId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  private readonly _pendingPaymentsCheckTimeoutMs = 10_000;

  private PAYMENT_ACCEPTED = "0";
  private PAYMENT_ABORTED = "9";
  private readonly TERMINAL_BUSY = 10;
  private readonly CANCELLATION_NOT_FOUND = 11;

  private _isSobitReady = signal(false);
  private _paymentsChecked = signal(true);
  private _pendingPaymentsOfflineOnly = signal(false);
  private _paymentUIDsInProgress = signal<Set<string>>(new Set());
  allowPaymentByCard = computed(() => {
    // return false;
    return this._isSobitReady() && this._paymentsChecked() && !this._pendingPaymentsOfflineOnly();
  });

  private getPaymentLockKey(paymentUID: string): string {
    return `${this._paymentLockStoragePrefix}${encodeURIComponent(paymentUID)}`;
  }

  private getPaymentSentKey(paymentUID: string): string {
    return `${this._paymentSentStoragePrefix}${encodeURIComponent(paymentUID)}`;
  }

  private getBrowserLockManager(): ILockManagerLike | null {
    return (this.document.defaultView?.navigator as Navigator & { locks?: ILockManagerLike })?.locks ?? null;
  }

  private async withBrowserPaymentLock<T>(paymentUID: string, callback: () => Promise<T>): Promise<T> {
    const lockManager = this.getBrowserLockManager();
    if (!lockManager) {
      return callback();
    }

    return lockManager.request(`sobit-payment-${paymentUID}`, { mode: "exclusive" }, callback);
  }

  private getPaymentLockStorage(): Storage | null {
    return this.document.defaultView?.localStorage ?? null;
  }

  private cleanupExpiredPaymentStorage(now = Date.now()): void {
    const storage = this.getPaymentLockStorage();
    if (!storage) {
      return;
    }

    const keysToRemove: string[] = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (!key) {
        continue;
      }

      const isLockKey = key.startsWith(this._paymentLockStoragePrefix);
      const isSentKey = key.startsWith(this._paymentSentStoragePrefix);
      if (!isLockKey && !isSentKey) {
        continue;
      }

      const rawValue = storage.getItem(key);
      if (!rawValue) {
        keysToRemove.push(key);
        continue;
      }

      try {
        const storedValue = JSON.parse(rawValue) as { createdAt?: number };
        const maxAge = isLockKey ? this._paymentLockMaxAgeMs : this._paymentSentMarkerMaxAgeMs;
        if (!storedValue.createdAt || now - storedValue.createdAt > maxAge) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => storage.removeItem(key));
  }

  private readPaymentLock(paymentUID: string): ISobitPaymentLock | null {
    const storage = this.getPaymentLockStorage();
    if (!storage) {
      return null;
    }

    const rawLock = storage.getItem(this.getPaymentLockKey(paymentUID));
    if (!rawLock) {
      return null;
    }

    try {
      return JSON.parse(rawLock) as ISobitPaymentLock;
    } catch {
      storage.removeItem(this.getPaymentLockKey(paymentUID));
      return null;
    }
  }

  private acquirePaymentLock(paymentUID: string): boolean {
    const storage = this.getPaymentLockStorage();
    const now = Date.now();

    if (!storage) {
      const currentLocks = this._paymentUIDsInProgress();
      if (currentLocks.has(paymentUID)) {
        return false;
      }

      this._paymentUIDsInProgress.set(new Set(currentLocks).add(paymentUID));
      return true;
    }

    this.cleanupExpiredPaymentStorage(now);

    if (this.hasPaymentBeenSentToTerminal(paymentUID, now)) {
      return false;
    }

    const lockKey = this.getPaymentLockKey(paymentUID);
    const existingLock = this.readPaymentLock(paymentUID);
    if (existingLock && now - existingLock.createdAt <= this._paymentLockMaxAgeMs) {
      return false;
    }

    const newLock: ISobitPaymentLock = {
      paymentUID,
      ownerId: this._paymentLockOwnerId,
      createdAt: now,
    };
    storage.setItem(lockKey, JSON.stringify(newLock));

    const savedLock = this.readPaymentLock(paymentUID);
    return savedLock?.ownerId === this._paymentLockOwnerId;
  }

  private hasPaymentBeenSentToTerminal(paymentUID: string, now = Date.now()): boolean {
    const storage = this.getPaymentLockStorage();
    if (!storage) {
      return false;
    }

    const sentKey = this.getPaymentSentKey(paymentUID);
    const rawMarker = storage.getItem(sentKey);
    if (!rawMarker) {
      return false;
    }

    try {
      const marker = JSON.parse(rawMarker) as ISobitPaymentSentMarker;
      if (now - marker.createdAt > this._paymentSentMarkerMaxAgeMs) {
        storage.removeItem(sentKey);
        return false;
      }

      return true;
    } catch {
      storage.removeItem(sentKey);
      return false;
    }
  }

  private markPaymentSentToTerminal(paymentUID: string): void {
    const storage = this.getPaymentLockStorage();
    if (!storage) {
      return;
    }

    this.cleanupExpiredPaymentStorage();

    const marker: ISobitPaymentSentMarker = {
      paymentUID,
      createdAt: Date.now(),
    };
    storage.setItem(this.getPaymentSentKey(paymentUID), JSON.stringify(marker));
  }

  private releasePaymentLock(paymentUID: string): void {
    const storage = this.getPaymentLockStorage();

    if (!storage) {
      const currentLocks = new Set(this._paymentUIDsInProgress());
      currentLocks.delete(paymentUID);
      this._paymentUIDsInProgress.set(currentLocks);
      return;
    }

    const lock = this.readPaymentLock(paymentUID);
    if (lock?.ownerId === this._paymentLockOwnerId) {
      storage.removeItem(this.getPaymentLockKey(paymentUID));
    }
  }

  private getSobitErrorCode(errorCode: unknown): SobitErrorCode | null {
    const normalized = Number(errorCode);

    switch (normalized) {
      case SobitErrorCode.ERROR_BAD_REQUEST:
      case SobitErrorCode.ERROR_TARGET_DEVICE_NOT_CONNECTED:
      case SobitErrorCode.ERROR_SERVER_ERROR:
      case SobitErrorCode.ERROR_TARGET_DEVICE_NOT_SET:
      case SobitErrorCode.ERROR_WRONG_CREDENTIALS:
      case SobitErrorCode.ERROR_WRONG_API_KEY:
      case SobitErrorCode.ERROR_DUPLICATE_CONNECTION:
      case SobitErrorCode.ERROR_TRANSACTION_NOT_FOUND:
        return normalized;
      default:
        return null;
    }
  }

  private getPaymentStatusForSobitStartError(errorCode: unknown): number {
    if (this.getSobitErrorCode(errorCode) === SobitErrorCode.ERROR_DUPLICATE_CONNECTION) {
      return PaymentStatusEnum.Pending;
    }

    return this.getSobitErrorCode(errorCode) ?? PaymentStatusEnum.Aborted;
  }

  private normalizeResultCode(resultCode: unknown): number | null {
    if (resultCode === null || resultCode === undefined || resultCode === "") {
      return null;
    }

    const normalized = Number(resultCode);
    return Number.isFinite(normalized) ? normalized : null;
  }

  private writeInfoLog(eventId: SobitErrorCode, payload?: unknown): void {
    void this._eftLogService.logInfo(eventId, payload as never);
  }

  private writeWarningLog(eventId: SobitErrorCode, payload?: unknown): void {
    void this._eftLogService.logWarning(eventId, payload as never);
  }

  private writeErrorLog(eventId: SobitErrorCode, payload?: unknown): void {
    void this._eftLogService.logError(eventId, payload as never);
  }

  private isSobitErrorResponse(value: unknown): value is { __sobitError: true; error: unknown } {
    return !!value && typeof value === "object" && (value as Record<string, unknown>).__sobitError === true;
  }

  private isSobitCheckTimeoutResponse(value: unknown): value is ISobitCheckTimeoutResponse {
    return !!value && typeof value === "object" && (value as Record<string, unknown>).__sobitTimeout === true;
  }

  private serializeErrorMessage(error: unknown): string {
    const normalized = this.normalizeError(error);

    if (typeof normalized === "string") {
      return normalized;
    }

    try {
      return JSON.stringify(normalized);
    } catch {
      return String(normalized);
    }
  }

  public loadSobitEcrScript(): Promise<void> {
    this.spinner.show();
    this.writeInfoLog(SobitErrorCode.SOBIT_LOAD_SCRIPT_START, {
      scriptUrl: "https://www.sobitecr.com/libs/SobitEcr.js",
      text: "https://www.sobitecr.com/libs/SobitEcr.js",
    });

    return new Promise((resolve, reject) => {
      const script = this.document.createElement("script");
      script.type = "text/javascript";
      script.src = "https://www.sobitecr.com/libs/SobitEcr.js";
      script.onload = () => {
        this.writeInfoLog(SobitErrorCode.SOBIT_LOAD_SCRIPT_SUCCESS);
        this.spinner.hide();
        resolve();
      };
      script.onerror = (error) => {
        this.writeErrorLog(SobitErrorCode.SOBIT_LOAD_SCRIPT_ERROR, {
          message: this.serializeErrorMessage(error),
          scriptUrl: script.src,
          text: script.src,
        });
        this.spinner.hide();
        reject(`Failed to load SobitEcr script: ${error}`);
      };
      this.document.head.appendChild(script);
    });
  }

  // Generate local instance of Client for using Sobit
  async generateClient(): Promise<void> {
    this.writeInfoLog(SobitErrorCode.SOBIT_GENERATE_CLIENT_START);
    const config = await this.getSobitConfig();

    if (!config?.apiKey || !config?.token) {
      this.writeWarningLog(SobitErrorCode.SOBIT_GENERATE_CLIENT_MISSING_CONFIG, {
        hasApiKey: !!config?.apiKey,
        hasToken: !!config?.token,
      });
    }

    if (typeof SobitEcr === "undefined" || !SobitEcr) {
      this.writeWarningLog(SobitErrorCode.SOBIT_CLIENT_NOT_READY, {
        reason: "SobitEcr script is not available",
      });
      this._sobitClient = null;
      this._isSobitReady.set(false);
      return;
    }

    const client = new SobitEcr(
      config?.apiKey,
      this._loginService.user?.SelectedBusinessUnit,
      config?.token,
    );

    this._sobitClient = client;

    if (config?.token && config?.apiKey) {
      this._isSobitReady.set(true);
      this._paymentsChecked.set(true);
      this.writeInfoLog(SobitErrorCode.SOBIT_GENERATE_CLIENT_READY, {
        businessUnit: this._loginService.user?.SelectedBusinessUnit,
      });
    }
  }

  async payByCard(request: PayByCardRequest): Promise<PayByCardResult> {
    return this.withBrowserPaymentLock(request.paymentUID, () => this.payByCardWithPaymentLock(request));
  }

  private async payByCardWithPaymentLock(request: PayByCardRequest): Promise<PayByCardResult> {
    if (!this.acquirePaymentLock(request.paymentUID)) {
      this.writeWarningLog(SobitErrorCode.SOBIT_PAY_BY_CARD_START, {
        paymentUID: request.paymentUID,
        journalNumber: request.paymentUID,
        message: "Payment already in progress for this paymentUID, ignoring duplicate call.",
      });
      return { outcome: "failed", resultCode: null, message: null };
    }

    return lastValueFrom(
      defer(() => this._payByCardInternal(request)).pipe(
        finalize(() => this.releasePaymentLock(request.paymentUID))
      )
    );
  }

  private async _payByCardInternal(request: PayByCardRequest): Promise<PayByCardResult> {
    const { paymentUID, valueToPay, currencyIsoCode, isReturn, formEmail, invNumber } = request;
    const showFailureDialog = request.showFailureDialog ?? true;
    this.spinner.show("paymentLoading");
    this.writeInfoLog(SobitErrorCode.SOBIT_PAY_BY_CARD_START, {
      paymentUID,
      journalNumber: paymentUID,
      valueToPay,
      currencyIsoCode,
      isReturn: !!isReturn,
      invNumber,
    });

    if (!this._sobitClient) {
      this.writeWarningLog(SobitErrorCode.SOBIT_CLIENT_NOT_READY, {
        paymentUID,
      });
      await this.generateClient();

      if (!this._sobitClient) {
        this.spinner.hide("paymentLoading");
        return { outcome: "failed", resultCode: null, message: null };
      }
    }

    const startTransactionData = {
      Amount: valueToPay.toString(),
      CurrencyCode: this.convertCurrencyIsoCodeToCurrencyCode(currencyIsoCode, paymentUID),
      Operation: isReturn ? "CR" : "CP",
      TransactionID: paymentUID,
      InvNumber: invNumber,
    } as SobitRequestData;

    const sobitResponse = await lastValueFrom(
      this.sendSobitTransaction(paymentUID, isReturn, startTransactionData).pipe(
        catchError((error: unknown) => {
          this.writeErrorLog(SobitErrorCode.SOBIT_PAY_BY_CARD_ERROR, {
            paymentUID,
            journalNumber: paymentUID,
            message: this.serializeErrorMessage(error),
            invNumber: invNumber,
          });

          return of({ __sobitError: true, error: this.normalizeError(error) } as const);
        }),
      ),
    );

    if (this.isSobitErrorResponse(sobitResponse)) {
      // Mark payments as not checked to disable further Sobit attempts
      this._paymentsChecked.set(false);

      const normalizedError = sobitResponse.error && typeof sobitResponse.error === "object"
        ? sobitResponse.error as Record<string, unknown>
        : {};
      const sobitErrorCode = normalizedError.code;
      const sobitErrorMessage = String(
        "message" in normalizedError
          ? normalizedError.message
          : "",
      );
      const startTransactionStatus = this.getPaymentStatusForSobitStartError(sobitErrorCode);
      const updatePaymentStatusRes = await this.updatePaymentStatus(paymentUID, {
        Result: startTransactionStatus,
        ErrorCode: sobitErrorCode,
        ErrorMessage: sobitErrorMessage,
      });

      if (!updatePaymentStatusRes) {
        this.writeErrorLog(SobitErrorCode.SOBIT_UPDATE_PAYMENT_STATUS_FAILED, {
          paymentUID,
          journalNumber: paymentUID,
          message: sobitErrorMessage,
          result: startTransactionStatus,
          invNumber,
          methodName: "startTransaction",
        });
      }

      const normalizedSobitErrorCode = this.getSobitErrorCode(sobitErrorCode);
      if (normalizedSobitErrorCode === SobitErrorCode.ERROR_DUPLICATE_CONNECTION) {
        this.spinner.hide("paymentLoading");
        const result = await this._popUpDialogInformalService.confirm({
          title: "webPos.Errors.Error",
          message: "webPos.PaymentWithCardFailed",
          subMessage: sobitErrorMessage || undefined,
          type: PopUpInformalType.Error,
          confirmButtonText: "webPos.PaymentStatus.CheckPaymentStatuses",
        });

        if (result?.confirmed) {
          await this.generateClient();
          this._paymentsRefreshRequested.next();
        }

        return {
          outcome: "handled",
          resultCode: normalizedSobitErrorCode,
          message: sobitErrorMessage || null,
        };
      }

      if (showFailureDialog) {
        const dialogMessage = isReturn
          ? this.translate.instant("webPos.ReturnOnCardFailed")
          : this.translate.instant("webPos.PaymentWithCardFailed");

        await this._popUpDialogInformalService.confirm({
          title: this.translate.instant("webPos.Errors.Error"),
          message: this.translate.instant(dialogMessage),
          subMessage: sobitErrorMessage,
          type: PopUpInformalType.Error,
        });
      }

      this.spinner.hide("paymentLoading");
      return {
        outcome: "failed",
        resultCode: this.normalizeResultCode(sobitErrorCode),
        message: sobitErrorMessage || null,
      };
    }

    this.writeInfoLog(SobitErrorCode.SOBIT_PAY_BY_CARD_RESPONSE, {
      paymentUID,
      journalNumber: paymentUID,
      message: sobitResponse?.RespMessage ?? null,
      result: sobitResponse?.Result,
      methodName: "startTransaction",
      invNumber,
    });
    const normalizedResultCode = this.normalizeResultCode(sobitResponse?.Result);
    const responseMessage = sobitResponse?.RespMessage ?? null;
    const updatePaymentStatusRes = await this.updatePaymentStatus(paymentUID, sobitResponse);
    if (!updatePaymentStatusRes) {
      this.writeErrorLog(SobitErrorCode.SOBIT_UPDATE_PAYMENT_STATUS_FAILED, {
        paymentUID,
        journalNumber: paymentUID,
        message: sobitResponse?.RespMessage ?? null,
        invNumber,
        methodName: "startTransaction",
      });
      this.spinner.hide("paymentLoading");
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.UpdatePaymentStatusesError"),
        type: PopUpInformalType.Error,
      });
      this.router.navigate(["/dashboard"]);
      return { outcome: "failed", resultCode: normalizedResultCode, message: responseMessage };
    }

    if (normalizedResultCode === Number(this.PAYMENT_ACCEPTED)) {
      this.writeInfoLog(SobitErrorCode.SOBIT_PAYMENT_ACCEPTED, {
        paymentUID,
        journalNumber: paymentUID,
        message: sobitResponse?.RespMessage ?? null,
        result: sobitResponse?.Result,
        invNumber,
        methodName: "startTransaction",
      });
      const dialogMessage = isReturn
        ? this.translate.instant("webPos.PaymentReturned")
        : this.translate.instant("webPos.PaymentDone");
      this.spinner.hide("paymentLoading");
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Done"),
        message: this.translate.instant(dialogMessage),
        subMessage: sobitResponse.RespMessage ?? "",
        type: PopUpInformalType.Success,
        showSendReceiptByEmail: false,
        emailAddress: formEmail, // From form
        autoCloseMs: 3000,
      });

      return { outcome: "accepted", resultCode: 0, message: responseMessage };
    }

    if (normalizedResultCode === this.TERMINAL_BUSY || normalizedResultCode === this.CANCELLATION_NOT_FOUND) {
      const dialogMessageKey = normalizedResultCode === this.TERMINAL_BUSY
        ? "webPos.SobitTerminalBusy"
        : "webPos.SobitCancellationNotFound";

      this.writeWarningLog(SobitErrorCode.SOBIT_TERMINAL_STATE_DIALOG_SHOWN, {
        paymentUID,
        journalNumber: paymentUID,
        invNumber: invNumber ?? null,
        result: normalizedResultCode,
        message: responseMessage,
        dialogMessage: dialogMessageKey,
        methodName: "startTransaction",
      });

      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant(dialogMessageKey),
        type: PopUpInformalType.Error,
        confirmButtonText: "webPos.Close",
      });

      this.writeInfoLog(SobitErrorCode.SOBIT_TERMINAL_STATE_DIALOG_CLOSED, {
        paymentUID,
        journalNumber: paymentUID,
        invNumber: invNumber ?? null,
        result: normalizedResultCode,
        message: responseMessage,
        userAction: "close",
        methodName: "startTransaction",
      });

      this.spinner.hide("paymentLoading");
      this._paymentsRefreshRequested.next();
      return {
        outcome: "handled",
        resultCode: normalizedResultCode,
        message: responseMessage,
      };
    }

    this.writeWarningLog(SobitErrorCode.SOBIT_PAYMENT_DECLINED, {
      paymentUID,
      journalNumber: paymentUID,
      message: sobitResponse?.RespMessage ?? null,
      result: sobitResponse?.Result,
      invNumber: invNumber ?? null,
      methodName: "startTransaction",
    });
    this.spinner.hide("paymentLoading");
    if (showFailureDialog) {
      const dialogMessage = isReturn
        ? this.translate.instant("webPos.ReturnOnCardFailed")
        : this.translate.instant("webPos.PaymentWithCardFailed");
      await this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant(dialogMessage),
        subMessage: sobitResponse.RespMessage ?? "",
        type: PopUpInformalType.Error,
      });
    }

    return { outcome: "failed", resultCode: normalizedResultCode, message: responseMessage };
  }

  async registerOfflineCardPayment(
    paymentUID: string,
    paymentAttributes?: Array<IPaymentAttribute> | null,
  ): Promise<boolean> {
    this.writeInfoLog(SobitErrorCode.SOBIT_REGISTER_OFFLINE_PAYMENT_START, {
      paymentUID,
      attributesCount: paymentAttributes?.length ?? 0,
    });
    const paymentAttributesMap = (paymentAttributes || []).reduce<Record<string, string>>((acc, attribute) => {
      const key = attribute?.Key?.toString?.();
      if (!key) {
        return acc;
      }

      acc[key] = attribute?.Value?.toString?.() || "";
      return acc;
    }, {});

    const offlinePaymentResponse: Record<string, string> = {
      Result: this.PAYMENT_ACCEPTED,
      Type: paymentAttributesMap.Type || "OFFLINE",
      CardPrefix: paymentAttributesMap.CardPrefix || "",
      CardSuffix: paymentAttributesMap.CardSuffix || "",
      AuthorizationCode: paymentAttributesMap.AuthorizationCode || "",
    };

    if (paymentAttributesMap.TenderType) {
      offlinePaymentResponse.TenderType = paymentAttributesMap.TenderType;
    }

    if (paymentAttributesMap.TenderSubType) {
      offlinePaymentResponse.TenderSubType = paymentAttributesMap.TenderSubType;
    }

    const updatePaymentStatusRes = await this.updatePaymentStatus(paymentUID, offlinePaymentResponse);

    this.writeInfoLog(SobitErrorCode.SOBIT_REGISTER_OFFLINE_PAYMENT_RESULT, {
      paymentUID,
      success: !!updatePaymentStatusRes,
    });

    return !!updatePaymentStatusRes;
  }

  private async updatePaymentStatus(paymentUID: string, response) : Promise<IExecuteProcedureResult<UpdatePaymentStatusModel> | null> {
    this.writeInfoLog(SobitErrorCode.SOBIT_UPDATE_PAYMENT_STATUS_START, {
      paymentUID,
      responseKeys: response ? Object.keys(response) : [],
      result: response?.Result ?? null,
    });
    const keyValuePairs = Object.entries(response).map(([key, value]) => ({
      Key: key,
      Value: value,
    }));

    const resCode = parseInt(response.Result, 10);

    if (isNaN(resCode) || isNullOrUndefined(resCode)) { 
      return null;
    }

    const updatePaymentRequest = this._sharedService.getUpdateDertPaymentRequest(
      resCode,
      paymentUID,
      keyValuePairs,
    );

    this.writeInfoLog(SobitErrorCode.SOBIT_UPDATE_PAYMENT_STATUS_REQUEST, {
      paymentUID,
      resCode,
      keyValuePairs,
    });

    // {"data":{"PaymentID":10477,"HeaderID":10495,"ToFinalDocuments":true},"result":0}
    const res: IExecuteProcedureResult<UpdatePaymentStatusModel> = await lastValueFrom(
      this._sharedApiService.executeProcedureObservable<IGetUpdateDertPaymentParams, IExecuteProcedureResult<UpdatePaymentStatusModel>>(updatePaymentRequest).pipe(
        catchError((error: unknown) => {
          this.writeErrorLog(SobitErrorCode.SOBIT_UPDATE_PAYMENT_STATUS_ERROR, {
            paymentUID,
            result: null,
            data: null,
            error: this.normalizeError(error),
            methodName: "updatePaymentStatus",
          });
          return of(null as IExecuteProcedureResult<UpdatePaymentStatusModel> | null);
        }),
      ),
    );

    if (!res) {
      return null;
    }

    this.writeInfoLog(SobitErrorCode.SOBIT_UPDATE_PAYMENT_STATUS_RESPONSE, {
      paymentUID,
      result: res?.result ?? null,
      data: res?.data ?? null,
    });

    if (res?.result === ResultCode.OK) {
      return res;
    }
    this.writeErrorLog(SobitErrorCode.SOBIT_UPDATE_PAYMENT_STATUS_ERROR, {
      paymentUID,
      result: res?.result ?? null,
      data: res?.data ?? null,
      methodName: "updatePaymentStatus",
    });
    await this._popUpDialogInformalService.confirm({
      title: this.translate.instant("webPos.Errors.Error"),
      message: this.translate.instant(
        "webPos.Errors.UpdatePaymentStatusesError",
      ),
      type: PopUpInformalType.Error,
    });
    return null;
  }

  async checkAndUpdatePaymentStatuses(paymentsToCheck: IPayment[]): Promise<PaymentStatusCheckOutcome> {
    const startedAt = Date.now();
    const deadline = startedAt + this._pendingPaymentsCheckTimeoutMs;
    this.writeInfoLog(SobitErrorCode.SOBIT_CHECK_PAYMENTS_START, {
      paymentsToCheckCount: paymentsToCheck?.length ?? 0,
    });

    if (!paymentsToCheck) {
      return this.completePendingPaymentsCheck(PaymentStatusCheckOutcome.Success, startedAt, 0);
    }

    const cardPaymentsToCheck = paymentsToCheck.filter(p => p.PaymentType === TenderType.Card || p.PaymentType === TenderType.ElectronicVoucher);
    const paymentUIDs = cardPaymentsToCheck.map(payment => payment.Id);
    console.info("[SobitPendingPayments] Verification started", {
      paymentCount: cardPaymentsToCheck.length,
      paymentUIDs,
      businessUnit: this._loginService.user?.SelectedBusinessUnit ?? null,
      timeoutMs: this._pendingPaymentsCheckTimeoutMs,
    });
    this.writeInfoLog(SobitErrorCode.SOBIT_CHECK_PAYMENTS_FILTERED, {
      cardPaymentsCount: cardPaymentsToCheck.length,
    });

    if (cardPaymentsToCheck.length === 0) {
      return this.completePendingPaymentsCheck(PaymentStatusCheckOutcome.Success, startedAt, 0);
    }

    if (!this._sobitClient) {
      this.writeWarningLog(SobitErrorCode.SOBIT_CLIENT_NOT_READY);
      const remainingTimeoutMs = Math.max(deadline - Date.now(), 1);
      const clientGenerationResult = await lastValueFrom(
        defer(() => this.generateClient()).pipe(
          timeout({ first: remainingTimeoutMs }),
          catchError((error: unknown) => of(error instanceof TimeoutError ? PaymentStatusCheckOutcome.Timeout : PaymentStatusCheckOutcome.Failed)),
        ),
      );

      if (clientGenerationResult === PaymentStatusCheckOutcome.Timeout) {
        return this.timeoutPendingPaymentsCheck(null, paymentUIDs, startedAt, 0);
      }

      if (clientGenerationResult === PaymentStatusCheckOutcome.Failed || !this._sobitClient) {
        console.error("[SobitPendingPayments] Verification failed; switching to offline-only mode", {
          paymentUID: null,
          errorCode: null,
          error: "Sobit client is not available",
        });
        return this.completePendingPaymentsCheck(PaymentStatusCheckOutcome.Failed, startedAt, 0);
      }
    }

    let paymentCheckedAndUpdated = true;
    let checkedCount = 0;
    for (const [index, payment] of cardPaymentsToCheck.entries()) {
      const remainingTimeoutMs = deadline - Date.now();
      if (remainingTimeoutMs <= 0) {
        return this.timeoutPendingPaymentsCheck(payment.Id, paymentUIDs.slice(index), startedAt, checkedCount);
      }

      console.info("[SobitPendingPayments] Checking payment", {
        paymentUID: payment.Id,
        index: index + 1,
        total: cardPaymentsToCheck.length,
        remainingTimeoutMs,
      });
      this.writeInfoLog(SobitErrorCode.SOBIT_CHECK_TRANSACTION_START, {
        paymentUID: payment.Id,
        methodName: "checkTransaction",
      });

      const response = await lastValueFrom(
        this.checkSobitTransaction(payment.Id).pipe(
          timeout({ first: remainingTimeoutMs }),
          catchError((error: unknown) => {
            if (error instanceof TimeoutError) {
              return of({ __sobitTimeout: true } as const);
            }

            const normalizedError = this.normalizeError(error);
            this.writeErrorLog(SobitErrorCode.SOBIT_CHECK_TRANSACTION_ERROR, {
              paymentUID: payment.Id,
              error: normalizedError,
              methodName: "checkTransaction",
            });

            console.error("[SobitPendingPayments] Verification failed; switching to offline-only mode", {
              paymentUID: payment.Id,
              errorCode: (normalizedError as Record<string, unknown>)?.code ?? null,
              error: normalizedError,
            });

            return of({ __sobitError: true, error: normalizedError } as const);
          }),
        ),
      );

      if (this.isSobitCheckTimeoutResponse(response)) {
        return this.timeoutPendingPaymentsCheck(payment.Id, paymentUIDs.slice(index), startedAt, checkedCount);
      }

      if (this.isSobitErrorResponse(response)) {
        const normalizedError = response.error as Record<string, unknown>;
        const errorCode = normalizedError?.code;
        const normalizedSobitErrorCode = this.getSobitErrorCode(errorCode);
        const checkedPaymentStatus = normalizedSobitErrorCode === SobitErrorCode.ERROR_TRANSACTION_NOT_FOUND
          ? PaymentStatusEnum.NotFound
          : PaymentStatusEnum.Pending;

        const statusResponse = {
          Result: checkedPaymentStatus,
          ErrorCode: errorCode,
          ErrorMessage: normalizedError?.message,
        };
        const updateResult = await this.updatePaymentStatus(payment.Id, statusResponse);
        if (updateResult) {
          payment.Status = checkedPaymentStatus;
        }

        if (normalizedSobitErrorCode === SobitErrorCode.ERROR_TRANSACTION_NOT_FOUND) {
          this.writeWarningLog(SobitErrorCode.SOBIT_TRANSACTION_NOT_FOUND, {
            paymentUID: payment.Id,
            errorCode,
            methodName: "checkTransaction",
          });
          checkedCount++;
          continue;
        }

        if (normalizedSobitErrorCode === SobitErrorCode.ERROR_WRONG_CREDENTIALS) {
          this.writeErrorLog(SobitErrorCode.SOBIT_WRONG_CREDENTIALS, {
            paymentUID: payment.Id,
            errorCode,
            methodName: "checkTransaction",
          });
        }

        return this.completePendingPaymentsCheck(PaymentStatusCheckOutcome.Failed, startedAt, checkedCount);
      }

      this.writeInfoLog(SobitErrorCode.SOBIT_CHECK_TRANSACTION_RESPONSE, {
        paymentUID: payment.Id,
        state: response?.state ?? null,
        hasParsedMessage: response?.parsedMessage !== null && response?.parsedMessage !== undefined,
        methodName: "checkTransaction",
      });

      if (response.state === 'cancelled' && !response.parsedMessage) {
        const updateResult = await this.updatePaymentStatus(
          payment.Id,
          { Result: this.PAYMENT_ABORTED, methodName: "checkTransaction" },
        );
        if (updateResult) {
          payment.Status = PaymentStatusEnum.Aborted;
        }
      }

      if (!response || response.parsedMessage === null || response.parsedMessage === undefined) {
        paymentCheckedAndUpdated = false;
        this._paymentsChecked.set(false);
        console.error("[SobitPendingPayments] Verification failed; switching to offline-only mode", {
          paymentUID: payment.Id,
          errorCode: null,
          error: "Sobit response did not contain parsedMessage",
        });
      } else {
        const updateResult = await this.updatePaymentStatus(
          payment.Id,
          { ...response.parsedMessage, methodName: "checkTransaction" },
        );
        const checkedResultCode = this.normalizeResultCode(response.parsedMessage.Result);
        if (updateResult && checkedResultCode !== null) {
          payment.Status = checkedResultCode as PaymentStatusEnum;
        }
      }
      checkedCount++;
    }

    return this.completePendingPaymentsCheck(
      paymentCheckedAndUpdated ? PaymentStatusCheckOutcome.Success : PaymentStatusCheckOutcome.Failed,
      startedAt,
      checkedCount,
    );
  }

  private timeoutPendingPaymentsCheck(
    activePaymentUID: string | null,
    pendingPaymentUIDs: string[],
    startedAt: number,
    checkedCount: number,
  ): PaymentStatusCheckOutcome {
    const durationMs = Date.now() - startedAt;
    const logDetails = {
      activePaymentUID,
      pendingPaymentUIDs,
      durationMs,
      checkedCount,
      offlineOnly: true,
    };

    console.warn("[SobitPendingPayments] Verification timed out; switching to offline-only mode", logDetails);
    this.writeWarningLog(SobitErrorCode.SOBIT_CHECK_TRANSACTION_TIMEOUT, logDetails);
    return this.completePendingPaymentsCheck(PaymentStatusCheckOutcome.Timeout, startedAt, checkedCount);
  }

  private completePendingPaymentsCheck(
    outcome: PaymentStatusCheckOutcome,
    startedAt: number,
    checkedCount: number,
  ): PaymentStatusCheckOutcome {
    const offlineOnly = outcome !== PaymentStatusCheckOutcome.Success;
    this._pendingPaymentsOfflineOnly.set(offlineOnly);
    this._paymentsChecked.set(!offlineOnly);
    const logDetails = {
      outcome,
      durationMs: Date.now() - startedAt,
      checkedCount,
      offlineOnly,
    };

    console.info("[SobitPendingPayments] Verification completed", logDetails);
    this.writeInfoLog(SobitErrorCode.SOBIT_CHECK_PAYMENTS_COMPLETE, logDetails);
    return outcome;
  }

  private sendSobitTransaction(
    paymentUID: string,
    isReturn = false,
    startTransactionData: SobitRequestData,
    logContext?: ISobitLogContext,
  ): Subject<any> {
    const operation = isReturn ? "CR" : "CP"; // CR for Credit Return, CP for Credit Payment
    const response: Subject<any> = new AsyncSubject();

    const requestData =  JSON.stringify(startTransactionData);

    this.markPaymentSentToTerminal(paymentUID);

    this.writeInfoLog(SobitErrorCode.SOBIT_START_TRANSACTION_REQUEST, {
      journalNumber: paymentUID,
      requestData: requestData,
      bnr: logContext?.bnr ?? null,
      variableNumber: logContext?.variableNumber ?? null,
      voucherNumber: logContext?.voucherNumber ?? null,
      methodName: "startTransaction",
    });

    this._sobitClient.startTransaction(
      requestData,
      paymentUID,
      (message) => {
        const parsedResponse = JSON.parse(message);
        this.writeInfoLog(SobitErrorCode.SOBIT_START_TRANSACTION_RESPONSE, {
          requestData: requestData,
          parsedResponse: parsedResponse,
          bnr: logContext?.bnr ?? null,
          variableNumber: logContext?.variableNumber ?? null,
          voucherNumber: logContext?.voucherNumber ?? null,
          methodName: "startTransaction",
        });
        response.next(parsedResponse);
        response.complete();
      },
      (code, message) => {
        this.writeErrorLog(SobitErrorCode.SOBIT_START_TRANSACTION_ERROR, {
          response: { code, message },
          requestData: requestData,
          bnr: logContext?.bnr ?? null,
          variableNumber: logContext?.variableNumber ?? null,
          voucherNumber: logContext?.voucherNumber ?? null,
          methodName: "startTransaction",
        });
        const error = new Error(`Error ${code}: ${message}`) as Error & { code: unknown };
        error.code = code;
        response.error(error);
        response.complete();
      },
      () => {
        this.writeInfoLog(SobitErrorCode.SOBIT_START_TRANSACTION_CONNECTION, {
          paymentUID,
          methodName: "startTransaction",
        });
      }
    );

    return response;
  }

  public checkSobitTransaction(paymentUID: string): Subject<any> {
    const response: Subject<any> = new AsyncSubject();

    this.writeInfoLog(SobitErrorCode.SOBIT_CHECK_TRANSACTION_REQUEST, {
      paymentUID,
      journalNumber: paymentUID,
      text: paymentUID,
      methodName: "checkTransaction",
    });

    if (!this._sobitClient) {
      this.generateClient();
    }

    this._sobitClient.checkTransaction(
      paymentUID,
      (state, message) => {

        if (message === null || message === undefined) { 
          this.writeInfoLog(SobitErrorCode.SOBIT_CHECK_TRANSACTION_RESPONSE, {
            paymentUID,
            journalNumber: paymentUID,
            state,
            text: paymentUID,
            methodName: "checkTransaction",
          });
          response.next({ state });
          response.complete();
          return;
        }

        const parsedMessage = JSON.parse(message);
        this.writeInfoLog(SobitErrorCode.SOBIT_CHECK_TRANSACTION_RESPONSE, {
          paymentUID,
          journalNumber: paymentUID,
          state,
          message,
          text: paymentUID,
          parsedMessage,
          methodName: "checkTransaction",
        });
        response.next({ state, parsedMessage});
        response.complete();
      },
      (code, message) => {
        this.writeErrorLog(SobitErrorCode.SOBIT_CHECK_TRANSACTION_ERROR, {
          paymentUID,
          journalNumber: paymentUID,
          code,
          message,
          text: paymentUID,
          methodName: "checkTransaction",
        });
        response.error({ code, message });
        response.complete();
      },
      (connect) => {
        if (connect === null || connect === undefined) {
          connect = connect === null ? 'null' : 'undefined';
        }
        console.log("Connection status changed: ", connect);
        this.writeInfoLog(SobitErrorCode.SOBIT_CONNECTION_STATUS, {
          paymentUID,
          message: String(connect),
          connect,
          methodName: "checkTransaction",
        });
      },
    );

    return response;
  }

  private async getSobitConfig(): Promise<ISobitConfig | null> {
    this.writeInfoLog(SobitErrorCode.SOBIT_GET_CONFIG_START);
    let sobitConfig: ISobitConfig = {
      apiKey: null,
      token: null,
    };

    const getSobitTokenRequest = this._sharedService.getSobitTokenRequest();

    const res = await lastValueFrom(
      this._sharedApiService.executeProcedureObservable(getSobitTokenRequest).pipe(
        catchError((error: unknown) => {
          this.writeErrorLog(SobitErrorCode.SOBIT_GET_CONFIG_ERROR, {
            error: this.normalizeError(error),
            methodName: "getSobitConfig",
          });
          return of(null as IExecuteProcedureResult<IGetSobitTokenResultData> | null);
        }),
      ),
    ) as IExecuteProcedureResult<IGetSobitTokenResultData> | null;

    if (!res) {
      this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.SobitScriptLoadError"),
        subMessage: this.translate.instant(
          "webPos.OnlyOfflinePaymentAvailable"
        ),
        type: PopUpInformalType.Error,
      });
      return null;
    }

    if (res.result !== 0 || !res?.data?.Token || !res?.data?.ApiKey) {
      this.writeWarningLog(SobitErrorCode.SOBIT_GET_CONFIG_INVALID, {
        result: res?.result ?? null,
        hasToken: !!res?.data?.Token,
        hasApiKey: !!res?.data?.ApiKey,
        methodName: "getSobitConfig",
      });
      this._popUpDialogInformalService.confirm({
        title: this.translate.instant("webPos.Errors.Error"),
        message: this.translate.instant("webPos.Errors.SobitScriptLoadError"),
        subMessage: this.translate.instant(
          "webPos.OnlyOfflinePaymentAvailable"
        ),
        type: PopUpInformalType.Error,
      });
    } else {
      sobitConfig.token = res.data.Token;
      sobitConfig.apiKey = res.data.ApiKey;
      this.writeInfoLog(SobitErrorCode.SOBIT_GET_CONFIG_SUCCESS, {
        hasToken: !!sobitConfig.token,
        hasApiKey: !!sobitConfig.apiKey,
        methodName: "getSobitConfig",
      });
    }

    return sobitConfig;
  }

  private normalizeError(error: unknown): unknown {
    if (error instanceof Error) {
      const normalizedError: Record<string, unknown> = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };

      for (const [key, value] of Object.entries(error)) {
        normalizedError[key] = value;
      }

      return normalizedError;
    }

    if (error && typeof error === "object") {
      const normalizedError: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(error as Record<string, unknown>)) {
        normalizedError[key] = value;
      }

      return normalizedError;
    }

    return error;
  }

  private convertCurrencyIsoCodeToCurrencyCode(currencyIsoCode: string, paymentUID: string): string {
    let currencyCode = null;

    // https://en.wikipedia.org/wiki/ISO_4217
    currencyIsoCode = currencyIsoCode.toLowerCase();
    switch (currencyIsoCode) {
      case 'czk':
        currencyCode = "203";
        break;
      case 'eur':
        currencyCode = "978";
        break;
      case 'ron':
        currencyCode = "946";
        break;
      case 'huf':
        currencyCode = "348";
        break;
      default:
        // Currently only CZK is supported
        console.warn(`Currency code ${currencyIsoCode} is not supported.`);
        this.writeWarningLog(SobitErrorCode.SOBIT_UNSUPPORTED_CURRENCY, {
          paymentUID,
          currencyIsoCode,
        });
        return currencyIsoCode;
    }
    return currencyIsoCode;
  }
}
